/* eslint-disable */
import apiClient from "@/services/axios";
import store from "store";
import Vue from "vue";
import { i18n } from "@/localization";
import tokenStorage from "@/services/tokenStorage";

export async function login(userName, password) {
  return apiClient
    .post("/admin-auth/login", {
      userName: userName,
      password: password,
    })
    .then((response) => {
      if (response) {
        // Check if MFA is required
        if (response.data.mfaToken) {
          const result = {
            mfaToken: response.data.mfaToken,
            mfaMethod: response.data.mfaMethod,
            phone: response.data.phone,
            userId: response.data.userId,
          };

          // If there's a warning (e.g., SMS rate limit), include it
          if (response.data.warning) {
            result.warning = response.data.warning;
          }

          return result;
        }

        // Normal login flow
        const { accessToken } = response.data;
        if (accessToken) {
          tokenStorage.set(accessToken); // Store in memory (not localStorage)
        }
        const { info, resourcesUrl } = response.data;
        return { info, resourcesUrl };
      }
    })
    .catch((err) => {
      let description = "";
      if (err.response && err.response.data && err.response.data.errorCode) {
        description = i18n.t(`errorCode.${err.response.data.errorCode}`);
      } else {
        description = i18n.t(`errorCode.AUTHENTICATION_ERROR`);
      }
      Vue.prototype.$notification.error({
        message: i18n.t("auth.loginFailed"),
        description: description,
      });
      return false;
    });
}

export async function qrLogin(token) {
  return apiClient
    .post("/admin-auth/qrcode/login", { token })
    .then((response) => {
      if (response) {
        if (response.data.mfaToken) {
          const result = {
            mfaToken: response.data.mfaToken,
            mfaMethod: response.data.mfaMethod,
            phone: response.data.phone,
            userId: response.data.userId,
          };
          if (response.data.warning) {
            result.warning = response.data.warning;
          }
          return result;
        }

        const { accessToken } = response.data;
        if (accessToken) {
          tokenStorage.set(accessToken);
        }
        const { info, resourcesUrl } = response.data;
        return { info, resourcesUrl };
      }
    })
    .catch((err) => {
      let description = "";
      if (err.response && err.response.data && err.response.data.errorCode) {
        description = i18n.t(`errorCode.${err.response.data.errorCode}`);
      } else {
        description = i18n.t(`errorCode.AUTHENTICATION_ERROR`);
      }
      Vue.prototype.$notification.error({
        message: i18n.t("auth.loginFailed"),
        description: description,
      });
      return false;
    });
}

export async function ssoLogin(token, userId) {
  //apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  return apiClient
    .post("/sso/edmanager", {
      token,
      userId,
    })
    .then((response) => {
      if (response) {
        const { token } = response.data;
        if (token) {
          tokenStorage.set(token); // Store in memory (not localStorage)
        }
        const { info } = response.data;
        return info;
      }
    })
    .catch((err) => {
      const { data } = err.response || {};
      let description = "";
      if (data?.errorCode) {
        description = i18n.t(`errorCode.${data.errorCode}`);
      } else {
        description = i18n.t("auth.genericError");
      }
      Vue.prototype.$notification.error({
        message: i18n.t("auth.ssoLoginFailed"),
        description: description,
      });
      return false;
    });
}

export async function register(email, password, name) {
  return apiClient
    .post("/auth/register", {
      email,
      password,
      name,
    })
    .then((response) => {
      if (response) {
        const { accessToken } = response.data;
        if (accessToken) {
          tokenStorage.set(accessToken); // Store in memory (not localStorage)
        }
        return response.data;
      }
      return false;
    });
}

export async function currentAccount(activeBuilding) {
  // Use /refresh endpoint instead of /verify
  // Refresh token is automatically sent via HTTP-only cookie
  const url = activeBuilding
    ? `/admin-auth/refresh?activeBuilding=${activeBuilding}`
    : "/admin-auth/refresh";

  return apiClient
    .post(url, {
      activeBuilding: activeBuilding, // Also pass as body parameter for compatibility
    })
    .then(({ data }) => {
      if (data.accessToken) {
        tokenStorage.set(data.accessToken); // Store in memory
      }
      return data;
    })
    .catch(async (err) => {
      // Refresh failed - logout and redirect to login
      const { data } = err.response || {};

      // Clear token storage
      tokenStorage.clear();

      // Dispatch logout to clear all state and redirect
      const vuexStore = (await import("@/store")).default;
      await vuexStore.dispatch("user/LOGOUT");

      // Show error notification
      let description = "";
      if (data?.errorCode) {
        description = i18n.t(`errorCode.${data.errorCode}`);
      } else {
        description = i18n.t(`errorCode.AUTHENTICATION_ERROR`);
      }
      Vue.prototype.$notification.error({
        message: i18n.t("auth.loginFailed"),
        description: description,
      });

      return false;
    });
}

export async function logout() {
  try {
    // Call logout API to revoke refresh token on server
    try {
      await apiClient.post("/admin-auth/logout", {});
    } catch (apiError) {
      // Even if API call fails, continue with local cleanup
      console.warn("Logout API call failed, continuing with local cleanup", apiError);
    }

    // Clean up in-memory token storage
    tokenStorage.clear(); // Clear access token from memory

    // Clean up other local storage (non-sensitive data)
    store.remove("app.settings.activeSchoolarYear");
    store.remove("DB");
    store.remove("app.settings.activeBuilding");
    store.remove("app.menu.selectedKeys");
    store.remove("app.topbar.favs");
    store.remove("app.settings.activeSchoolarYear");
    localStorage.clear();
    return true;
  } catch {
    return false;
  }
}

// MFA Functions
export async function setupMFA(method) {
  return apiClient
    .post("/admin-auth/mfa/setup", { method })
    .then((response) => response.data)
    .catch((err) => {

      Vue.prototype.$notification.error({
        message: i18n.t("mfa.setupError"),
        description: i18n.t("mfa.setupErrorDescription"),
      });
      throw err;
    });
}

export async function enableMFA(method, code) {
  // Get current socket ID to keep current session active
  const socketId = Vue.prototype.$socket?.client?.id || null;

  return apiClient
    .post("/admin-auth/mfa/enable", { method, code, socketId })
    .then((response) => response.data)
    .catch((err) => {

      Vue.prototype.$notification.error({
        message: i18n.t("mfa.enableError"),
        description: i18n.t("mfa.enableErrorDescription"),
      });
      throw err;
    });
}

export async function disableMFA(password) {
  return apiClient
    .post("/admin-auth/mfa/disable", { password })
    .then((response) => response.data)
    .catch((err) => {

      Vue.prototype.$notification.error({
        message: i18n.t("mfa.disableError"),
        description: i18n.t("mfa.disableErrorDescription"),
      });
      throw err;
    });
}

export async function verifyMFA(mfaToken, code) {
  return apiClient
    .post("/admin-auth/mfa/verify", { mfaToken, code })
    .then((response) => {
      if (response.data.accessToken) {
        tokenStorage.set(response.data.accessToken);
      }
      return response.data;
    })
    .catch((err) => {
      const { data } = err.response || {};

      Vue.prototype.$notification.error({
        message: i18n.t("mfa.invalidCode"),
        description: i18n.t("mfa.invalidCodeDescription"),
      });
      throw err;
    });
}

export async function resendSMSOTP(mfaToken) {
  return apiClient
    .post("/admin-auth/mfa/resend-sms", { mfaToken })
    .then((response) => response.data)
    .catch((err) => {
      const { data } = err.response || {};
      const errorCode = data?.errorCode;
      const waitMinutes = data?.error?.details?.waitMinutes;
      // Handle rate limit error with wait time
      if (errorCode === "SMS_OTP_RATE_LIMIT" && waitMinutes) {
        const minutesText = waitMinutes === 1
          ? i18n.t("mfa.rateLimitMessageSingular", { minutes: waitMinutes })
          : i18n.t("mfa.rateLimitMessage", { minutes: waitMinutes });
        Vue.prototype.$notification.error({
          message: i18n.t("mfa.rateLimitExceeded"),
          description: minutesText,
          duration: 8, // Show for 8 seconds to give user time to read
        });
      } else {
        let description = "";
        if (data?.errorCode) {
          description = i18n.t(`errorCode.${data.errorCode}`);
        } else {
          description = i18n.t("mfa.smsErrorDescription");
        }
        Vue.prototype.$notification.error({
          message: i18n.t("mfa.smsError"),
          description: description,
        });
      }
      throw err;
    });
}
